import $ from 'jquery';
import { eventChannel } from 'redux-saga';

import { BASE_URL } from './constants';

// Ensure jQuery is globally available for SignalR plugin BEFORE it loads
window.jQuery = window.$ = $;

const HUB_URL = `${BASE_URL}/signalr/hubs`;

let signalrLoaded = false;

/**
 * Dynamically loads ms-signalr-client (must happen after jQuery is global).
 */
async function ensureSignalRLoaded() {
  if (signalrLoaded) return;
  await import('ms-signalr-client');
  signalrLoaded = true;
}

/**
 * Normalizes a SignalR PascalCase notification to camelCase store shape.
 * SignalR sends: { Type, Display, Alert, Event, Title, Message, Icon, DismissAfter, Metadata }
 * Store expects: { notificationType, notificationDisplay, notificationAlert, notificationEvent, title, message, ... }
 */
export function normalizeSignalRNotification(raw) {
  return {
    id: raw.Id || raw.id || Date.now(),
    notificationType: raw.Type || raw.type || 'activity',
    notificationDisplay: raw.Display || raw.display || 'badge',
    notificationAlert: raw.Alert || raw.alert || 'info',
    notificationEvent: raw.Event || raw.event || '',
    title: raw.Title || raw.title || '',
    message: raw.Message || raw.message || '',
    iconType: raw.Icon || raw.icon || '',
    autoDismissMs: raw.DismissAfter || raw.dismissAfter || 0,
    metadata: raw.Metadata || raw.metadata || {},
    isRead: false,
    createdAt: new Date().toISOString(),
    readAt: null,
  };
}

/**
 * Creates a Redux-Saga event channel that emits normalized SignalR notifications.
 * @param {string} apiKey - The API key for authentication
 * @returns {EventChannel} A redux-saga event channel
 */
export function createNotificationChannel(apiKey) {
  return eventChannel((emitter) => {
    let connection;

    ensureSignalRLoaded()
      .then(() => {
        connection = $.hubConnection(HUB_URL, {
          qs: { apiKey },
          logging: false,
        });

        const hub = connection.createHubProxy('NotificationHub');

        hub.on('receiveSystemNotification', (notification) => {
          const normalized = normalizeSignalRNotification(notification);
          emitter(normalized);
        });

        // Real-time AI video generation progress (pushed by Core from the VideoWorker callbacks)
        hub.on('receiveVideoProgress', (data) => {
          emitter({
            __videoProgress: true,
            videoId: data?.videoId ?? data?.VideoId,
            progress: data?.progress ?? data?.Progress ?? 0,
            step: data?.step ?? data?.Step ?? '',
            status: data?.status ?? data?.Status ?? '',
            message: data?.message ?? data?.Message ?? '',
          });
        });

        connection.disconnected(() => {
          // Attempt reconnect after 5s
          setTimeout(() => {
            connection.start({ withCredentials: false, transport: ['webSockets', 'serverSentEvents', 'longPolling'] }).fail(() => {});
          }, 5000);
        });

        connection
          .start({ withCredentials: false, transport: ['webSockets', 'serverSentEvents', 'longPolling'] })
          .done(() => {
            console.log('[SignalR] Connected to NotificationHub');
          })
          .fail((err) => {
            console.error('[SignalR] Connection failed:', err);
          });
      })
      .catch((err) => {
        console.error('[SignalR] Failed to load SignalR client:', err);
      });

    // Return unsubscribe/cleanup function
    return () => {
      if (connection) {
        connection.stop();
      }
    };
  });
}
